import { ImagePlus, X } from "lucide-react";
import { useRef, type RefObject } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface BrandingVariantState {
  /** Currently persisted logo URL resolver (returns absolute URL); null if no logo stored. */
  existingLogoSrc: string | null;
  /** Locally selected file preview (object URL); null when no file is staged. */
  pendingLogoPreview: string | null;
  /** True when existing URL failed to load (fallback UX). */
  logoLoadError: boolean;
  /** Hex color value currently in the form (editable). */
  color: string;
  /** Empty-color sentinel. When falsy the swatch falls back to transparent. */
  colorEmpty?: boolean;
}

export interface BrandingVariantHandlers {
  onPickFile: (file: File) => void;
  onClearPendingFile: () => void;
  onColorChange: (hex: string) => void;
  onLogoLoadError: () => void;
}

interface BrandingVariantCardProps {
  variant: "light" | "dark";
  enterpriseName: string;
  state: BrandingVariantState;
  handlers: BrandingVariantHandlers;
  disabled: boolean;
  allowedLogoMimeTypes: ReadonlySet<string>;
  maxLogoBytes: number;
  fallbackBg: string;
  onSizeError: () => void;
  onFormatError: () => void;
}

function BrandingVariantCard({
  variant,
  enterpriseName,
  state,
  handlers,
  disabled,
  allowedLogoMimeTypes,
  maxLogoBytes,
  fallbackBg,
  onSizeError,
  onFormatError,
}: BrandingVariantCardProps): JSX.Element {
  const { t } = useTranslation(undefined, { keyPrefix: "platformEnterprises" });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const previewSrc =
    state.pendingLogoPreview ??
    (state.logoLoadError ? null : state.existingLogoSrc);
  const swatch = state.colorEmpty || !state.color ? fallbackBg : state.color;
  const isDark = variant === "dark";

  return (
    <div
      className={
        isDark
          ? "rounded-xl border border-border/80 bg-slate-950 p-4 text-slate-50 shadow-sm"
          : "rounded-xl border border-border bg-white p-4 text-slate-900 shadow-sm"
      }
    >
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide opacity-70">
          {t(isDark ? "brandingCardDark" : "brandingCardLight")}
        </p>
      </div>

      <BrandingPreview
        bgColor={swatch}
        logoSrc={previewSrc}
        alt={enterpriseName}
        onLogoError={handlers.onLogoLoadError}
        isDark={isDark}
      />

      <div className="mt-4 space-y-3">
        <div className="space-y-1.5">
          <Label className={isDark ? "text-slate-200" : "text-slate-900"}>
            {t("fieldLogo")}
          </Label>
          <div className="flex items-center gap-3">
            <LogoThumb
              src={previewSrc}
              alt={enterpriseName}
              isDark={isDark}
              onError={handlers.onLogoLoadError}
              onPick={() => fileInputRef.current?.click()}
              onClear={
                state.pendingLogoPreview ? handlers.onClearPendingFile : undefined
              }
              deleteLabel={t("common_delete", { defaultValue: "Eliminar" })}
              disabled={disabled}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className={isDark ? "bg-slate-900 text-slate-50" : "bg-white text-slate-900 border-slate-300"}
            >
              {previewSrc ? t("logoChange") : t("logoUpload")}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (!allowedLogoMimeTypes.has(file.type)) {
                  onFormatError();
                  return;
                }
                if (file.size > maxLogoBytes) {
                  onSizeError();
                  return;
                }
                handlers.onPickFile(file);
              }}
            />
          </div>
          <p
            className={
              isDark
                ? "text-xs text-slate-400"
                : "text-xs text-slate-500"
            }
          >
            {t(isDark ? "logoDarkHint" : "logoHint")}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label
            htmlFor={`brand-color-${variant}`}
            className={isDark ? "text-slate-200" : "text-slate-900"}
          >
            {t("fieldPrimaryColor")}
          </Label>
          <div className="flex items-center gap-2">
            <input
              id={`brand-color-${variant}`}
              type="color"
              value={state.color || "#000000"}
              onChange={(e) => handlers.onColorChange(e.target.value)}
              disabled={disabled}
              className={`h-9 w-12 cursor-pointer rounded-md border p-0.5 ${isDark ? "border-input bg-background" : "border-slate-300 bg-white"}`}
            />
            <Input
              value={state.color}
              onChange={(e) => handlers.onColorChange(e.target.value)}
              disabled={disabled}
              placeholder="#000000"
              className={`font-mono flex-1 ${isDark ? "bg-slate-900 text-slate-50" : "bg-white text-slate-900 border-slate-300"}`}
              maxLength={16}
            />
          </div>
          {isDark ? (
            <p className="text-xs text-slate-400">
              {t("primaryColorDarkHint")}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function BrandingPreview({
  bgColor,
  logoSrc,
  alt,
  onLogoError,
  isDark,
}: {
  bgColor: string;
  logoSrc: string | null;
  alt: string;
  onLogoError: () => void;
  isDark: boolean;
}): JSX.Element {
  return (
    <div
      className="flex h-20 items-center justify-center rounded-lg border border-black/5 transition-colors duration-150 dark:border-white/10"
      style={{ backgroundColor: bgColor }}
    >
      {logoSrc ? (
        <img
          src={logoSrc}
          alt={alt}
          onError={onLogoError}
          className="max-h-10 max-w-[60%] object-contain"
        />
      ) : (
        <span
          className={`text-xs font-medium ${isDark ? "text-white/70" : "text-black/60"}`}
        >
          {alt}
        </span>
      )}
    </div>
  );
}

function LogoThumb({
  src,
  alt,
  isDark,
  onError,
  onPick,
  onClear,
  deleteLabel,
  disabled,
}: {
  src: string | null;
  alt: string;
  isDark: boolean;
  onError: () => void;
  onPick: () => void;
  onClear?: () => void;
  deleteLabel: string;
  disabled: boolean;
}): JSX.Element {
  if (!src) {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={deleteLabel}
        className={
          isDark
            ? "flex h-12 w-12 cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-slate-700 bg-slate-900 transition-colors hover:border-slate-500"
            : "flex h-12 w-12 cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-border bg-muted/30 transition-colors hover:border-primary/50 hover:bg-muted/50"
        }
        onClick={onPick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onPick();
        }}
      >
        <ImagePlus
          className={
            isDark
              ? "h-5 w-5 text-slate-500"
              : "h-5 w-5 text-muted-foreground/60"
          }
        />
      </div>
    );
  }
  return (
    <div className="relative shrink-0">
      <img
        src={src}
        alt={alt}
        onError={onError}
        className={
          isDark
            ? "h-12 w-12 rounded-md border border-slate-800 bg-slate-900 object-contain p-1"
            : "h-12 w-12 rounded-md border border-border bg-muted/30 object-contain p-1"
        }
      />
      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          disabled={disabled}
          className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow"
          aria-label={deleteLabel}
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  );
}

export interface EnterpriseBrandingEditorProps {
  enterpriseName: string;
  light: BrandingVariantState & BrandingVariantHandlers;
  dark: BrandingVariantState & BrandingVariantHandlers;
  disabled: boolean;
  allowedLogoMimeTypes: ReadonlySet<string>;
  maxLogoBytes: number;
  onSizeError: () => void;
  onFormatError: () => void;
}

/**
 * Side-by-side preview + controls for an enterprise's light and dark brand
 * variants. Each card shows the logo rendered on its primary color so admins
 * can validate contrast before saving.
 */
export function EnterpriseBrandingEditor(
  props: EnterpriseBrandingEditorProps,
): JSX.Element {
  const { light, dark, ...shared } = props;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <BrandingVariantCard
        variant="light"
        enterpriseName={shared.enterpriseName}
        state={light}
        handlers={light}
        disabled={shared.disabled}
        allowedLogoMimeTypes={shared.allowedLogoMimeTypes}
        maxLogoBytes={shared.maxLogoBytes}
        fallbackBg="#f4f4f5"
        onSizeError={shared.onSizeError}
        onFormatError={shared.onFormatError}
      />
      <BrandingVariantCard
        variant="dark"
        enterpriseName={shared.enterpriseName}
        state={dark}
        handlers={dark}
        disabled={shared.disabled}
        allowedLogoMimeTypes={shared.allowedLogoMimeTypes}
        maxLogoBytes={shared.maxLogoBytes}
        fallbackBg="#0f172a"
        onSizeError={shared.onSizeError}
        onFormatError={shared.onFormatError}
      />
    </div>
  );
}

// Re-export types so pages can shape their state handlers without reaching
// into the component file directly.
export type { RefObject };
